import { Badge, Callout, Card, EmptyState, Stat, Table, Td } from '../ui/primitives';
import { dateTime, pct } from '../../lib/format';
import type { ProviderRow, SeedTestRow } from '../../lib/types';

export interface MicrosoftReport {
  microsoft: ProviderRow | null;
  comparison: {
    microsoftAcceptanceRate: number | null;
    otherAcceptanceRate: number;
    microsoftBounceRate: number | null;
    otherBounceRate: number;
  };
  diagnostics: Array<{
    code: string;
    category: string;
    title: string;
    explanation: string;
    portal: string | null;
    count: number;
  }>;
  seedTests: SeedTestRow[];
}

/**
 * Microsoft deep-dive.
 *
 * Microsoft is where most B2B lead-gen mail goes and where it most often
 * quietly disappears, so it gets its own view: acceptance separated from
 * placement, its rejection codes explained, and the seed tests that are the
 * only real evidence of inbox versus junk.
 */
export function MicrosoftPanel({ report }: { report: MicrosoftReport }) {
  const ms = report.microsoft;

  return (
    <div className="space-y-5">
      <Callout tone="info" title="What these numbers do and do not say">
        Acceptance comes from Microsoft returning a success code at the gateway - it proves the message
        was taken, nothing more. Inbox versus Junk can only be established by reading a mailbox we own
        inside Microsoft, which is what the seed tests below do.
      </Callout>

      {!ms ? (
        <EmptyState
          title="No Microsoft-hosted recipients in this period"
          body="Recipients are classified by MX record, so Microsoft 365 business domains are grouped here automatically once mail goes out."
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Sent to Microsoft" value={ms.sent.toLocaleString()} />
            <Stat
              label="Accepted by Microsoft"
              value={pct(ms.acceptanceRate)}
              hint={
                ms.accepted.toLocaleString() +
                ' messages. Other providers: ' +
                pct(report.comparison.otherAcceptanceRate)
              }
              tone={ms.acceptanceRate >= 95 ? 'good' : ms.acceptanceRate >= 85 ? 'warn' : 'bad'}
            />
            <Stat
              label="Deferred (throttling)"
              value={ms.deferred.toLocaleString()}
              hint="Sustained 4.7.5xx deferrals are Microsoft slowing an unfamiliar sender - the earliest warning available."
              tone={ms.deferred > ms.sent * 0.25 ? 'warn' : undefined}
            />
            <Stat
              label="Blocked on reputation"
              value={ms.blocked.toLocaleString()}
              hint="Refused at connection time. These never reached a mailbox at all."
              tone={ms.blocked > 0 ? 'bad' : 'good'}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              label="Microsoft inbox placement"
              value={ms.inboxRate === null ? 'Not measured' : pct(ms.inboxRate, 0)}
              hint={
                ms.inboxRate === null
                  ? 'Add a Microsoft seed mailbox under Deliverability to measure this.'
                  : 'From the most recent seed test, sample size ' + ms.seedSampleSize
              }
              tone={ms.inboxRate === null ? undefined : ms.inboxRate >= 85 ? 'good' : 'warn'}
            />
            <Stat
              label="Human open rate"
              value={pct(ms.openRate.filtered)}
              hint={pct(ms.openRate.raw) + ' before automated activity was removed'}
            />
            <Stat
              label="Complaint rate"
              value={pct(ms.complaintRate, 3)}
              hint="Microsoft starts filtering hard above 0.3%."
              tone={ms.complaintRate >= 0.3 ? 'bad' : ms.complaintRate >= 0.1 ? 'warn' : 'good'}
            />
          </div>
        </>
      )}

      <Card
        title="Microsoft rejection codes seen"
        subtitle="Each code is matched against the Exchange Online Protection catalogue and explained with the remediation steps."
      >
        {report.diagnostics.length === 0 ? (
          <EmptyState title="No Microsoft rejections recorded" />
        ) : (
          <Table head={['Code', 'Category', 'What it means', 'Count', 'Action']}>
            {report.diagnostics.map((d) => (
              <tr key={d.code}>
                <Td>
                  <code className="rounded bg-ink-100 px-1.5 py-0.5 text-xs font-medium text-ink-800">
                    {d.code}
                  </code>
                </Td>
                <Td>
                  <Badge tone={d.category === 'reputation_block' ? 'bad' : d.category === 'throttling' ? 'warn' : 'neutral'}>
                    {d.category.replace(/_/g, ' ')}
                  </Badge>
                </Td>
                <Td className="max-w-xl">
                  <div className="font-medium text-ink-900">{d.title}</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{d.explanation}</p>
                </Td>
                <Td className="tabular-nums">{d.count.toLocaleString()}</Td>
                <Td>
                  {d.portal ? (
                    <a
                      href={d.portal}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-sky-700 underline"
                    >
                      Open portal
                    </a>
                  ) : (
                    <span className="text-xs text-ink-400">--</span>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="Seed tests - measured folder placement"
        subtitle="A tokenised probe is sent from the real sending identity to mailboxes we control, then those mailboxes are read to see where it landed."
      >
        {report.seedTests.length === 0 ? (
          <EmptyState
            title="No seed tests run yet"
            body="Configure at least one Outlook.com and one Microsoft 365 seed mailbox, then run a test from the domain page."
          />
        ) : (
          <Table head={['Test', 'Domain', 'Started', 'Inbox', 'Junk', 'Never appeared', 'Status']}>
            {report.seedTests.map((t) => (
              <tr key={t.id}>
                <Td className="font-medium text-ink-900">{t.name}</Td>
                <Td className="font-mono text-xs">{t.domain}</Td>
                <Td className="text-xs">{dateTime(t.startedAt)}</Td>
                <Td>
                  <Badge tone="good">{t.inbox}</Badge>
                </Td>
                <Td>{t.junk > 0 ? <Badge tone="warn">{t.junk}</Badge> : '0'}</Td>
                <Td>{t.missing > 0 ? <Badge tone="bad">{t.missing}</Badge> : '0'}</Td>
                <Td>
                  <Badge tone={t.status === 'completed' ? 'neutral' : 'info'}>{t.status}</Badge>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
